import React, { useEffect, useMemo, useRef } from 'react';
import { Center } from '@mantine/core';
import { useNavigate, useSearchParams } from 'react-router';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { CalendarPlatform } from 'src/meetings/models';
import PageLoader from 'src/UI/loader/PageLoader';
import { useCreateRemoteDocsConnectionMutation } from 'src/secret-menu/sharepoint/hooks/useCreateRemoteDocsConnectionMutation';

type Props = {
  platform: CalendarPlatform;
};

export const DocsOAuthRedirectPage = ({ platform }: Props) => {
  const [searchParams] = useSearchParams();
  const { createRemoteDocsConnection } = useCreateRemoteDocsConnectionMutation();
  const navigate = useNavigate();

  const code = useMemo(() => searchParams.get('code') ?? '', [searchParams]);
  const state = useMemo(() => searchParams.get('state') ?? '', [searchParams]);
  const error = useMemo(() => searchParams.get('error') ?? '', [searchParams]);

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (error && !hasRunRef.current) {
      hasRunRef.current = true;
      notifyAndLogError(
        'The connection was not successful',
        `There was an error connecting to your account. The provider returned the error: ${error}. Please try again or contact support if the issue persists.`,
        new Error(`OAuth error: ${error}`),
      );
      navigate('/secret-menu/sharepoint');
      return;
    }
  }, [error, navigate]);

  useEffect(() => {
    if (!code || hasRunRef.current) return;
    hasRunRef.current = true;
    (async () => {
      try {
        await createRemoteDocsConnection({ authCode: code, platform, state });
        navigate('/secret-menu/sharepoint');
      } catch (error) {
        notifyAndLogError('Failed to connect', 'Something went wrong. Please try again.', error);
        navigate('/secret-menu/sharepoint');
      }
    })();
  }, [code, createRemoteDocsConnection, navigate]);

  return (
    <Center h="100%">
      <PageLoader />
    </Center>
  );
};
